import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { apiClient } from '@/api/api';
import type {
  Chat,
  Message,
  VoiceInputState,
  Survey,
  ChatbotMessageDetail,
  IncomingChatbotMessage
} from '../types/chat';
import { ChatWebSocketService } from '../services/websocket';

interface BasicSpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface BasicSpeechRecognitionResult {
  0: BasicSpeechRecognitionAlternative;
  isFinal: boolean;
}

interface BasicSpeechRecognitionEvent {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: BasicSpeechRecognitionResult;
  };
}

interface BasicSpeechRecognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: (event: BasicSpeechRecognitionEvent) => void;
  onerror: ((event: Event) => void) | null;
  start: () => void;
  stop: () => void;
}

type SpeechRecognitionConstructor = new () => BasicSpeechRecognition;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

const STORAGE_KEY = 'chatHistory'
const isClient = typeof window !== 'undefined'

export const useChatStore = defineStore('chat', () => {
  // State
  const isOpen = ref(false);
  const currentChat = ref<Chat | null>(null);
  const chatHistory = ref<Chat[]>([]);
  const isLoading = ref(false);
  const voiceInput = ref<VoiceInputState>({
    isActive: false,
    isListening: false,
    transcript: '',
    confidence: 0
  });
  const recognitionInstance = ref<BasicSpeechRecognition | null>(null);
  type PersistedChat = Omit<Chat, 'createdAt' | 'updatedAt'> & {
    createdAt: string;
    updatedAt: string;
  };

  const searchQuery = ref('');
  const showHistory = ref(false);
  const activeSurvey = ref<Survey | null>(null);

  const getWebSocketUrl = (baseUrl: string): string => {
    let wsUrl = baseUrl;
    if (wsUrl.startsWith('http://')) {
      wsUrl = wsUrl.replace('http://', 'ws://');
    } else if (wsUrl.startsWith('https://')) {
      wsUrl = wsUrl.replace('https://', 'wss://');
    }
    return `${wsUrl}websocket/chat`;
  };

  const apiBaseUrl = apiClient.defaults.baseURL ?? '';
  const wsService = new ChatWebSocketService(getWebSocketUrl(apiBaseUrl));

  // Computed
  const filteredChatHistory = computed(() => {
    if (!searchQuery.value) return chatHistory.value;
    return chatHistory.value.filter(chat =>
      chat.name.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  });

  const hasMessages = computed(() => {
    return currentChat.value && currentChat.value.messages.length > 0;
  });

  // Actions
  const openChat = () => {
    isOpen.value = true;
    if (!currentChat.value) {
      createNewChat();
    }
    connectWebSocket();
  };

  const closeChat = () => {
    isOpen.value = false;
    wsService.disconnect();
    window.removeEventListener('chatbot-message', handleChatbotMessage);
  };

  const createNewChat = () => {
    const newChat: Chat = {
      id: generateId(),
      name: 'Новый чат',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      isSaved: false
    };
    currentChat.value = newChat;
  };

  const sendMessage = async (content: string, type: 'text' | 'voice' | 'image' | 'text image', imageData?: string | null, imageFile?: File | null) => {
    if (!currentChat.value) return;

    const userMessage: Message = {
      id: generateId(),
      content,
      type: 'user',
      timestamp: new Date(),
      image: imageData || undefined,
      imageFile: imageFile || undefined
    };

    currentChat.value.messages.push(userMessage);
    updateChatName(content);

    isLoading.value = true;

    // Send via WebSocket
    const messageData: any = {
      type: 'message',
      chatId: currentChat.value.id,
      content,
      messageType: type
    };

    // Add image data if present
    if (imageData) {
      messageData.imageData = {
        imageData: imageData,
        ...(imageFile && {
          fileName: imageFile.name,
          fileSize: imageFile.size,
          fileType: imageFile.type
        })
      };
    }

    wsService.send(messageData);
  };

  const receiveMessage = (messageData: IncomingChatbotMessage) => {
    if (!currentChat.value) return;

    const botMessage: Message = {
      id: generateId(),
      content: messageData.content,
      type: 'bot',
      timestamp: new Date(),
      isFormatted: messageData.isFormatted,
      productCard: messageData.productCard,
      links: messageData.links,
      suggestions: messageData.suggestions,
      surveyId: messageData.surveyId,
      image: messageData.image,
      reactions: {}
    };

    currentChat.value.messages.push(botMessage);
    currentChat.value.updatedAt = new Date();
    isLoading.value = false;
  };

  const handleChatbotMessage: EventListener = (event) => {
    const detail = (event as CustomEvent<ChatbotMessageDetail>).detail;
    if (!detail) {
      return;
    }

    if (detail.type === 'message') {
      receiveMessage(detail);
    } else if (detail.type === 'survey') {
      showSurvey(detail.survey);
    }
  };

  const reactToMessage = (messageId: string, reaction: 'like' | 'dislike') => {
    if (!currentChat.value) return;

    const message = currentChat.value.messages.find(m => m.id === messageId);
    if (message && message.type === 'bot') {
      if (!message.reactions) message.reactions = {};

      if (reaction === 'like') {
        message.reactions.liked = !message.reactions.liked;
        message.reactions.disliked = false;
      } else {
        message.reactions.disliked = !message.reactions.disliked;
        message.reactions.liked = false;
      }

      // Send reaction to server
      wsService.send({
        type: 'reaction',
        messageId,
        reaction: message.reactions.liked ? 'like' : message.reactions.disliked ? 'dislike' : null
      });
    }
  };

  const copyMessage = (content: string) => {
    if (!isClient || !navigator?.clipboard) {
      return
    }
    navigator.clipboard.writeText(content);
  };

  const persistChatHistory = () => {
    if (!isClient) {
      return
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(chatHistory.value))
  }

  const saveChat = () => {
    if (!currentChat.value) return;

    currentChat.value.isSaved = true;
    const existingIndex = chatHistory.value.findIndex(chat => chat.id === currentChat.value!.id);

    if (existingIndex >= 0) {
      chatHistory.value[existingIndex] = { ...currentChat.value };
    } else {
      chatHistory.value.unshift({ ...currentChat.value });
    }

    persistChatHistory();
  };

  const loadChat = (chatId: string) => {
    const chat = chatHistory.value.find(c => c.id === chatId);
    if (chat) {
      currentChat.value = { ...chat };
      showHistory.value = false;
    }
  };

  const deleteChat = (chatId: string) => {
    chatHistory.value = chatHistory.value.filter(chat => chat.id !== chatId);
    persistChatHistory();

    if (currentChat.value?.id === chatId) {
      createNewChat();
    }
  };

  const renameChat = (chatId: string, newName: string) => {
    const chat = chatHistory.value.find(c => c.id === chatId);
    if (chat) {
      chat.name = newName;
      chat.updatedAt = new Date();
      persistChatHistory();

      if (currentChat.value?.id === chatId) {
        currentChat.value.name = newName;
        currentChat.value.updatedAt = new Date();
      }
    }
  };

  const toggleFavorite = (chatId: string) => {
    const chat = chatHistory.value.find(c => c.id === chatId);
    if (chat) {
      chat.isFavorite = !chat.isFavorite;
      chat.updatedAt = new Date();
      persistChatHistory();

      if (currentChat.value?.id === chatId) {
        currentChat.value.isFavorite = chat.isFavorite;
        currentChat.value.updatedAt = new Date();
      }
    }
  };

  const startVoiceInput = () => {
    if (!isClient) {
      return
    }

    const SpeechRecognitionCtor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognitionInstance.value = recognition;

    voiceInput.value.isActive = true;
    voiceInput.value.isListening = true;
    voiceInput.value.transcript = '';
    voiceInput.value.confidence = 0;

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'ru-RU';

    recognition.onresult = (event: BasicSpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptPart = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcriptPart;
        } else {
          interimTranscript += transcriptPart;
        }
      }

      const latestResult = event.results[event.results.length - 1]?.[0];
      voiceInput.value.transcript = finalTranscript || interimTranscript;
      voiceInput.value.confidence = latestResult?.confidence ?? 0;
    };

    recognition.onerror = () => {
      stopVoiceInput();
    };

    recognition.start();
  };

  const stopVoiceInput = () => {
    recognitionInstance.value?.stop();
    recognitionInstance.value = null;
    voiceInput.value.isActive = false;
    voiceInput.value.isListening = false;
    voiceInput.value.confidence = 0;
  };

  const sendVoiceMessage = () => {
    if (voiceInput.value.transcript.trim()) {
      sendMessage(voiceInput.value.transcript, 'voice');
      voiceInput.value.transcript = '';
      stopVoiceInput();
    }
  };

  const showSurvey = (survey: Survey) => {
    activeSurvey.value = survey;
  };

  const hideSurvey = () => {
    activeSurvey.value = null;
  };

  const connectWebSocket = async () => {
    if (!isClient) {
      return
    }

    try {
      await wsService.connect();
      window.removeEventListener('chatbot-message', handleChatbotMessage);
      window.addEventListener('chatbot-message', handleChatbotMessage);
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      // Fallback to mock responses for demo
      setTimeout(() => {
        receiveMessage({
          type: 'message',
          content: 'Извините, не удается подключиться к серверу. Работаю в демо-режиме.',
          isFormatted: false
        });
      }, 1000);
    }
  };

  const updateChatName = (firstMessage: string) => {
    if (!currentChat.value || currentChat.value.messages.length > 1) return;

    const shortName = firstMessage.length > 30
      ? firstMessage.substring(0, 30) + '...'
      : firstMessage;
    currentChat.value.name = shortName;
  };

  const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  // Load chat history from localStorage on init
  const loadChatHistory = () => {
    if (!isClient) {
      return
    }

    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as PersistedChat[];
        chatHistory.value = parsed.map((chat) => ({
          ...chat,
          createdAt: new Date(chat.createdAt),
          updatedAt: new Date(chat.updatedAt)
        }));
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  // Initialize
  loadChatHistory();

  return {
    // State
    isOpen,
    currentChat,
    chatHistory,
    isLoading,
    voiceInput,
    searchQuery,
    showHistory,
    activeSurvey,

    // Computed
    filteredChatHistory,
    hasMessages,

    // Actions
    openChat,
    closeChat,
    createNewChat,
    sendMessage,
    receiveMessage,
    reactToMessage,
    copyMessage,
    saveChat,
    loadChat,
    deleteChat,
    renameChat,
    toggleFavorite,
    startVoiceInput,
    stopVoiceInput,
    sendVoiceMessage,
    showSurvey,
    hideSurvey
  };
});
